import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveBalanceService } from '../services/leaveBalanceService';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { trackBeginCheckout } from '../lib/analytics';

/** 制限付きログイン（#11）の理由表示。顧客に「なぜ使えないか」を必ず伝える。 */
const RESTRICTION_MESSAGES: Record<string, string> = {
  TRIAL_EXPIRED: '無料トライアル期間が終了しました。',
  SUBSCRIPTION_CANCELED: 'サブスクリプションは解約されています。',
  SUBSCRIPTION_EXPIRED: 'サブスクリプションの有効期間が終了しました。',
  PAYMENT_PAST_DUE: 'お支払いを確認できないため、ご利用を停止しています。',
  SUBSCRIPTION_INACTIVE: 'サブスクリプションが有効ではありません。',
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const FILENAME_PATTERN = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

// UTC文字列をJST形式で表示するヘルパー関数
const formatJST = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

interface PaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface SubscriptionStatus {
  subscription_status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'beta';
  subscription_plan: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  max_users: number;
  current_user_count: number;
  current_price: number;
  has_payment_method: boolean;
  payment_method?: PaymentMethod | null;
  canceled_at?: string | null;
  deletion_scheduled_at?: string | null;
  cancellation_reason?: string | null;
  data_exported?: boolean;
  user_role?: string; // ユーザーの役割（master, admin, manager, employee）
}

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [ledgerDownloading, setLedgerDownloading] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
    } catch (error) {
      console.error('サブスクリプション情報取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPayment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/subscription/create-checkout-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      trackBeginCheckout('new');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('支払い情報設定エラー:', error);
      setMessage('支払い情報の設定に失敗しました');
    }
  };

  // ベータ → 有料 転換（master のセルフサーブ）
  // beta解除+短期trial付与+Checkout起動をサーバ側で原子的に行い、返却URL（カード登録）へ遷移する。
  const handleConvertFromBeta = async () => {
    try {
      setConverting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/subscription/convert-from-beta`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      trackBeginCheckout('from_beta');
      // カード登録（Checkout setupモード）へリダイレクト
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('有料移行エラー:', error);
      setMessage(error.response?.data?.error || '有料プランへの移行に失敗しました');
      setConverting(false);
      setShowConvertModal(false);
    }
  };

  // 請求ポータル（Stripe Billing Portal）を開く。請求書/領収書DL・カード更新等をテナント自身で管理。
  const handleOpenBillingPortal = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/subscription/billing-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('請求ポータル起動エラー:', error);
      setMessage(error.response?.data?.error || '請求ポータルの起動に失敗しました');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/subscription/cancel`,
        { reason: cancellationReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // トライアル期間中の解約（猶予期間なし）の場合、即座にログアウト
      if (response.data.grace_period_days === 0) {
        // localStorageをクリア
        localStorage.removeItem('token');
        localStorage.removeItem('companyCode');
        localStorage.removeItem('username');
        localStorage.removeItem('password');

        // ログイン画面にリダイレクト
        alert('サブスクリプションを解約しました。ご利用ありがとうございました。');
        navigate('/login');
        return;
      }

      // 有料プランの解約の場合は通常通り
      setMessage(response.data.message);
      setShowCancelModal(false);
      setCancellationReason('');
      fetchStatus();
    } catch (error: any) {
      console.error('解約エラー:', error);
      setMessage(error.response?.data?.error || 'サブスクリプションの解約に失敗しました');
    }
  };

  const handleReactivateSubscription = async () => {
    if (!confirm('サブスクリプションを再開しますか？')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/subscription/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(response.data.message);
      fetchStatus();
    } catch (error: any) {
      console.error('再開エラー:', error);
      setMessage(error.response?.data?.error || 'サブスクリプションの再開に失敗しました');
    }
  };

  /**
   * 年次有給休暇管理簿（CSV）をダウンロードする。
   *
   * この画面にも置いているのは、**解約後に確実に辿り着ける唯一の画面**だから。
   * 休暇残数管理（/leave-balances）は requireActiveSubscription の内側にあり、
   * 解約すると画面の読み込み自体が 403 になる。
   * 一方このダウンロードは requireExportAccess 側なので、削除予定日までは通る。
   */
  const handleDownloadLedger = async () => {
    try {
      setLedgerDownloading(true);
      await leaveBalanceService.downloadAnnualPaidLedger();
    } catch (error: any) {
      console.error('年次有給休暇管理簿の出力に失敗しました:', error);
      setMessage(error.response?.data?.error || '年次有給休暇管理簿の出力に失敗しました');
    } finally {
      setLedgerDownloading(false);
    }
  };

  /**
   * ステータス表記。
   *
   * トライアル期限が切れても DB の subscription_status は 'trialing' のままなので、
   * 文字列だけを見ると期限切れの顧客に「トライアル中」と言い続けることになる。
   * 期限を過ぎたかどうか（trialEnded）を呼び出し側から渡して表記を分ける。
   */
  const getStatusText = (status: string, trialEnded = false) => {
    switch (status) {
      case 'trialing':
        return trialEnded ? 'トライアル終了' : 'トライアル中';
      case 'active':
        return '有効';
      case 'past_due':
        return '支払い遅延';
      case 'canceled':
        return 'キャンセル済み';
      case 'beta':
        return 'ベータテスト';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string, trialEnded = false) => {
    switch (status) {
      case 'trialing':
        return trialEnded ? '#dc3545' : '#17a2b8';
      case 'active':
        return '#28a745';
      case 'past_due':
        return '#ffc107';
      case 'canceled':
        return '#dc3545';
      case 'beta':
        return '#6f42c1';
      default:
        return '#6c757d';
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>読み込み中...</div>;
  }

  if (!status) {
    return <div style={{ padding: '20px' }}>サブスクリプション情報を取得できませんでした</div>;
  }

  const trialDaysLeft = status.trial_ends_at
    ? Math.ceil((new Date(status.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  /**
   * トライアル期限を過ぎたか。
   *
   * 残日数は期限を過ぎると負になり、Math.ceil(-0.7) は -0 になる。
   * 「あと0日」と表示され、なおかつ `<= 3` が真なので「まもなく終了」も出続けていた。
   * 終了済みかどうかを先に判定し、残日数の表示自体を切り替える。
   */
  const trialEnded =
    status.subscription_status === 'trialing' &&
    !!status.trial_ends_at &&
    new Date(status.trial_ends_at).getTime() <= Date.now();

  /**
   * 解約ボタンを出せる状態か。
   * トライアル中でカード未登録の場合、そもそも解約するものが無いので出さない。
   */
  const canCancelSubscription =
    !status.canceled_at &&
    status.subscription_status !== 'beta' &&
    !(status.subscription_status === 'trialing' && !status.has_payment_method);

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <header style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>サブスクリプション管理</h1>
          </div>
          {!user?.accessRestricted && (
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ダッシュボードに戻る
            </button>
          )}
        </div>

        {/* 制限付きログイン中の説明（#11）。理由と、できることを明示する */}
        {user?.accessRestricted && (
          <div
            style={{
              padding: '15px',
              backgroundColor: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffeeba',
              borderRadius: '4px',
              marginBottom: '15px',
              fontSize: '14px',
            }}
          >
            <strong>{RESTRICTION_MESSAGES[user.restrictionCode ?? 'SUBSCRIPTION_INACTIVE']}</strong>
            <p style={{ margin: '8px 0 0' }}>
              現在ご利用いただけるのは、この画面での<strong>お申し込み・お支払い方法の更新</strong>と、
              下部の<strong>データのダウンロード</strong>（出勤簿・年次有給休暇管理簿）です。
            </p>
          </div>
        )}
      </header>

      {message && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '20px',
          }}
        >
          {message}
        </div>
      )}

      {/* ステータスカード */}
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px',
        }}
      >
        <h3>現在のプラン</h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>ステータス:</strong>
            <span
              style={{
                marginLeft: '10px',
                padding: '4px 12px',
                borderRadius: '12px',
                backgroundColor: getStatusColor(status.subscription_status, trialEnded),
                color: 'white',
                fontSize: '14px',
              }}
            >
              {getStatusText(status.subscription_status, trialEnded)}
            </span>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>ユーザー数:</strong> {status.current_user_count}人
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>月額料金:</strong> ¥{status.current_price.toLocaleString()}
            <div style={{ marginTop: '5px', fontSize: '12px', color: '#6c757d', marginLeft: '0' }}>
              ※ 10名まで1,000円、11名以降は1名につき+100円となります。追加利用分は自動的に計算され請求されます。
            </div>
          </div>
          {status.subscription_status === 'trialing' && trialDaysLeft !== null && (
            <div style={{ marginBottom: '10px' }}>
              {trialEnded ? (
                <>
                  <strong>トライアル:</strong>{' '}
                  <span style={{ color: '#dc3545', fontWeight: 'bold' }}>終了しました</span>
                  {status.trial_ends_at && (
                    <span style={{ marginLeft: '5px', fontSize: '14px', color: '#6c757d' }}>
                      （終了日時: {formatJST(status.trial_ends_at)}）
                    </span>
                  )}
                </>
              ) : (
                <>
                  <strong>トライアル残日数:</strong> あと{trialDaysLeft}日
                  {status.trial_ends_at && (
                    <span style={{ marginLeft: '5px', fontSize: '14px', color: '#6c757d' }}>
                      （終了日時: {formatJST(status.trial_ends_at)}）
                    </span>
                  )}
                  {trialDaysLeft <= 3 && (
                    <span style={{ marginLeft: '10px', color: '#dc3545', fontWeight: 'bold' }}>
                      まもなく終了
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          {status.current_period_end && (
            <div style={{ marginBottom: '10px' }}>
              <strong>次回請求日:</strong> {formatJST(status.current_period_end)}
            </div>
          )}
        </div>
      </div>

      {/* ベータ → 有料 移行CTA（ベータテナントの master のみ） */}
      {status.subscription_status === 'beta' && status.user_role === 'master' && (
        <div
          style={{
            backgroundColor: '#ede7f6',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #6f42c1',
          }}
        >
          <h3 style={{ marginTop: 0 }}>有料プランへ移行する</h3>
          <p style={{ fontSize: '14px', color: '#4a3a6b', lineHeight: 1.7 }}>
            現在はベータ利用中です。クレジットカードを登録すると有料プランに切り替わり、ベータ終了後も継続してご利用いただけます。
            移行してもデータや設定はそのまま引き継がれ、<strong>従業員のログインや打刻が止まることはありません</strong>。
          </p>
          <button
            onClick={() => setShowConvertModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            有料プランに移行する
          </button>
        </div>
      )}

      {/* 支払い情報設定（トライアル中のみ表示、ベータは除外） */}
      {status.subscription_status === 'trialing' && !status.has_payment_method && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #ffc107',
          }}
        >
          <h3>支払い情報を登録してください</h3>
          <p>トライアル期間終了後もサービスを継続利用するには、支払い情報の登録が必要です。</p>
          <button
            onClick={handleSetupPayment}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            クレジットカードを登録する
          </button>
        </div>
      )}

      {/* クレジットカード情報表示 */}
      {status.has_payment_method && status.payment_method && (
        <div
          style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px',
          }}
        >
          <h3>お支払い方法</h3>
          <div style={{ marginTop: '10px' }}>
            <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '10px' }}>💳</span>
              <strong style={{ textTransform: 'capitalize' }}>{status.payment_method.brand}</strong>
              <span style={{ marginLeft: '10px' }}>•••• {status.payment_method.last4}</span>
              <span style={{ marginLeft: '10px', fontSize: '14px', color: '#6c757d' }}>
                有効期限: {status.payment_method.exp_month}/{status.payment_method.exp_year}
              </span>
            </div>
            <button
              onClick={handleSetupPayment}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '10px',
              }}
            >
              カード情報を変更する
            </button>
          </div>
        </div>
      )}

      {/* 請求・お支払いの管理（Billing Portal） master/admin かつ 課金開始済のみ */}
      {(status.user_role === 'master' || status.user_role === 'admin') && status.has_payment_method && (
        <div
          style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px',
          }}
        >
          <h3>請求・お支払いの管理</h3>
          <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px', lineHeight: 1.7 }}>
            請求書・領収書のダウンロードや、クレジットカード情報の更新ができます。
          </p>
          <button
            onClick={handleOpenBillingPortal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            請求ポータルを開く
          </button>
        </div>
      )}

      {/* 解約情報表示 */}
      {status.canceled_at && (
        <div
          style={{
            backgroundColor: '#f8d7da',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb',
          }}
        >
          <h3 style={{ color: '#721c24' }}>⚠️ 解約予約済み</h3>
          <div style={{ marginTop: '10px' }}>
            <div style={{ marginBottom: '10px' }}>
              <strong>解約日時:</strong> {formatJST(status.canceled_at)}
            </div>
            {status.current_period_end && (
              <div style={{ marginBottom: '10px' }}>
                <strong>利用可能期限:</strong>{' '}
                <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                  {formatJST(status.current_period_end)}
                </span>
                <div style={{ fontSize: '14px', color: '#721c24', marginTop: '5px' }}>
                  ※ この日時を過ぎるとアクセスできなくなります
                </div>
              </div>
            )}
          </div>
          <button
            onClick={handleReactivateSubscription}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              marginTop: '10px',
            }}
          >
            サブスクリプションを再開する
          </button>
        </div>
      )}

      {/* データのダウンロード
        *
        * 2026-08-22 に「全データエクスポート（JSON / 生CSV / SQL）」を廃止した。
        * 顧客が読めない生ダンプで、規約が約束した目的を果たしていなかったため。
        * 代わりに**平常時から使っている2つの帳票**へ誘導する。
        * どちらも requireExportAccess 側にあるので、解約後30日でも利用できる。
        */}
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px',
        }}
      >
        <h3>データのダウンロード</h3>
        <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px', lineHeight: 1.8 }}>
          <strong>解約前に必ず取得してください。</strong>
          解約後もデータ削除予定日まではダウンロードできます。
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/attendance-export')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            勤怠データ（出勤簿）を出力
          </button>
          <button
            onClick={handleDownloadLedger}
            disabled={ledgerDownloading}
            style={{
              padding: '10px 20px',
              backgroundColor: ledgerDownloading ? '#94A3B8' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: ledgerDownloading ? 'not-allowed' : 'pointer',
            }}
          >
            {ledgerDownloading ? '出力中…' : '年次有給休暇管理簿 (CSV)'}
          </button>
        </div>
      </div>

      {/* 管理者機能（master かつ、中に出すものがあるときだけ）
        *
        * 枠を user_role だけで出していたため、唯一の中身である解約ブロックが
        * 条件で消えると「管理者機能」という見出しだけの空箱が残っていた。
        * 表示条件を中身と揃える。
        */}
      {status.user_role === 'master' && canCancelSubscription && (
        <div
          style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <h3>管理者機能</h3>

          {/* サブスクリプション解約（表示条件は canCancelSubscription が持つ） */}
          <div>
            <h4>サブスクリプション解約</h4>
            <p style={{ fontSize: '14px', color: '#6c757d' }}>
              サブスクリプションを解約すると、全てのデータが削除されます。
            </p>
            <button
              onClick={() => setShowCancelModal(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              サブスクリプションを解約する
            </button>
          </div>
        </div>
      )}

      {/* 有料移行 確認モーダル */}
      {showConvertModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !converting && setShowConvertModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '520px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>有料プランへの移行</h3>
            <p style={{ lineHeight: 1.7 }}>
              次の画面でクレジットカードを登録すると、有料プランに切り替わります。
            </p>
            <ul style={{ fontSize: '14px', color: '#555', lineHeight: 1.8, paddingLeft: '20px' }}>
              <li>移行後もデータ・設定・アカウントはそのまま引き継がれます。</li>
              <li>従業員のログインや打刻は中断されません。</li>
              <li>料金は10名まで月1,000円（11名以降は1名につき+100円）です。</li>
            </ul>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConvertModal(false)}
                disabled={converting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: converting ? 'not-allowed' : 'pointer',
                  opacity: converting ? 0.6 : 1,
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleConvertFromBeta}
                disabled={converting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: converting ? 'not-allowed' : 'pointer',
                  opacity: converting ? 0.6 : 1,
                }}
              >
                {converting ? '移行中...' : 'カード登録に進む'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 解約確認モーダル */}
      {showCancelModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowCancelModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>サブスクリプション解約確認</h3>
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
              ⚠️ サブスクリプションを解約します
            </p>
            {status.subscription_status === 'trialing' ? (
              <p>
                <strong style={{ color: '#dc3545' }}>トライアル期間中の解約は即座に実行されます。</strong>
                <br />
                解約後はすぐにアクセスできなくなり、すべてのデータが削除されます。
              </p>
            ) : (
              <p>
                サブスクリプションを解約すると、次回請求日まで引き続きご利用いただけます。
                <br />
                その後、すべてのデータが削除されます。
              </p>
            )}
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                解約理由（任意）
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="解約理由をご記入ください"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  resize: 'vertical',
                }}
              />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationReason('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleCancelSubscription}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                解約する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
